import { useState, useRef, CSSProperties } from 'react';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import Loader from './Loader';
import { styx } from 'modules/Utils';
import Icon from 'elements/Icon';
import Sty from 'modules/Sty';
import Colors from 'modules/Colors';

interface UploadCsvFileProps {
    className?: string;
    style?: CSSProperties;
    onFileUpload: (file: File) => void;
    label?: string;
    isLoading?: boolean;
}

const FileChooser = ({
    onFileUpload,
    label = 'Upload CSV File',
    className,
    style,
    isLoading = false
}: UploadCsvFileProps) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    /** Handler for when files are dropped or selected */
    const onDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setSelectedFile(file);
            onFileUpload(file);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv']
        },
        multiple: false
    });

    const handleRemoveFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div
            className={clsx('fileChooserElement', className)}
            style={styx(
                sty.fileChooserElement,
                isDragActive && sty.dragActive,
            )}
            {...getRootProps()}
        >
            <input className="fileChooserInput" {...getInputProps()} ref={fileInputRef} />

            {isLoading ?
                <Loader
                    className="fileChooserLoader"
                    title="Uploading file..."
                />
                :
                selectedFile ?
                    <div className="fileChooserFileInfoContainer" style={styx(sty.fileChooserUploadText, style)}>
                        <span style={sty.fileName}>{selectedFile.name}</span>
                        <div
                            className="onClickForStopPropagation"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile();
                            }}
                        >
                            <Icon
                                className="fileChooserRemoveIcon"
                                icon="trash"
                                color={Colors.greyDark}
                            />
                        </div>
                    </div>
                    :
                    <div className="fileChooserUploadText" style={styx(sty.fileChooserUploadText, style)}>
                        <Icon
                            className="fileChooserUploadIcon"
                            icon="upload"
                        />
                        <div style={{ ...Sty.text16purple }}>
                            {isDragActive ? 'Drop the CSV file here' : label}
                        </div>
                    </div>
            }
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    fileChooserElement: {
        backgroundColor: 'transparent',
    },
    dragActive: {
        backgroundColor: Colors.greenLight,
        borderRadius: 16,
    },
    fileChooserUploadText: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 16,
        cursor: 'pointer',
        borderRadius: 16,
        border: `2px dashed ${Colors.grey}`,
    },
    fileChooserFileInfoContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: 16,
        borderRadius: 16
    },
    fileName: {
        ...Sty.text16grey,
        wordBreak: 'break-all'
    },
};

export default FileChooser;
